import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ACTION_FLAGS, useMe } from '@features/user-profile/hooks/useMe';
import { useAnyConnectSurfaceOpen } from '@features/accounts/state/connectDialogStore';
import { hasSeenConnectedPopup, markConnectedPopupSeen } from '../lib/cqgCreds';
import { CqgConnectedMessage } from './CqgConnectedMessage';
import { BRAND_GRADIENT } from '@shared/styles/brand';

/**
 * "Setting up your concurrent login" — the one-time 48h notice (Account Sync Workflow spec 4.A). It
 * shows AFTER we've identified that the user's CQG login can't run the journal and their trading
 * platform at once — i.e. a detected LIVE `concurrent_session` conflict on which they chose to keep
 * the platform (CqgReconnectPopup "Not now" / the dialog take-over "Cancel"), which submits the
 * concurrent-login request. Keys off the durable `concurrent_request_submitted` action flag, which is
 * now only ever set on that live-conflict request — so this NEVER fires on a clean connect (2026-07-18:
 * the old proactive "on every first go-live" trigger was removed). Shows at most ONCE
 * (`hasSeenConnectedPopup`) and never once the concurrent login is established. Suppressed while a
 * connect dialog is open — that dialog carries its own inline notice (`ConcurrentNoticeView`), and the
 * decline path marks the notice seen so the two never both fire. Mounted once in AppShell.
 */
export function CqgConnectedPopup() {
  const me = useMe();
  const flags = me.data?.actionFlags;
  const submitted = flags?.includes(ACTION_FLAGS.concurrentRequestSubmitted) ?? false;
  const established = flags?.includes(ACTION_FLAGS.concurrentLoginEstablished) ?? false;
  const connectSurfaceOpen = useAnyConnectSurfaceOpen();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!submitted || established || connectSurfaceOpen) return;
    if (hasSeenConnectedPopup()) return;
    markConnectedPopupSeen();
    setShow(true);
  }, [submitted, established, connectSurfaceOpen]);

  if (!show) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="Setting up your concurrent login">
      <div className="w-full max-w-md rounded-2xl bg-canvas p-6 text-text shadow-2xl">
        <h2 className="text-[17px] font-bold">Setting up your concurrent login</h2>

        {/* Exact copy (Account Sync Workflow spec 4.A) — shared with the dialog's inline notice. */}
        <div className="mt-3">
          <CqgConnectedMessage />
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={() => setShow(false)}
            className="rounded-lg px-4 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
            style={{ background: BRAND_GRADIENT }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
